import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';
import { FaCheckDouble, FaCircle, FaClipboardList, FaChevronLeft, FaChevronRight, FaEllipsisV, FaExclamationTriangle, FaPaperPlane, FaPencilAlt, FaPlus, FaSearch, FaTimes, FaUserMinus, FaUserPlus, FaUsers } from 'react-icons/fa';
import { MdCheck, MdContentCopy, MdDoneAll, MdOutlineAttachFile } from 'react-icons/md';
import { BiMessageSquareDetail } from 'react-icons/bi';
import { BsPersonCircle } from 'react-icons/bs';
import { io as socketIO } from 'socket.io-client';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL, getNotifURL } from '../../utils/api';

// ─────────────────────────────────────────────
// Global Styles
// ─────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', 'Segoe UI', sans-serif; background: #f0f2f5; }
`;

// ─────────────────────────────────────────────
// Animations
// ─────────────────────────────────────────────
const fadeIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); }`;
const highlightPulse = keyframes`
  0%   { background: rgba(91,155,213,0.25); }
  60%  { background: rgba(91,155,213,0.18); }
  100% { background: transparent; }
`;
const slideIn = keyframes`from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); }`;
const pulse = keyframes`0%,100% { transform: scale(1); } 50% { transform: scale(1.15); }`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const LoaderWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 48px 24px;
  width: 100%;
`;
const SpinRing = styled.span`
  display: inline-block;
  width: ${({ $size }) => $size || 40}px;
  height: ${({ $size }) => $size || 40}px;
  border-radius: 50%;
  border: 3px solid rgba(30,58,95,0.12);
  border-top-color: #1e3a5f;
  animation: ${spin} 0.75s linear infinite;
`;
const LoaderLabel = styled.p`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  letter-spacing: 0.01em;
`;

// ─────────────────────────────────────────────
// Layout
// ─────────────────────────────────────────────
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f0f2f5;
  overflow: hidden;
`;

const TopHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #1e3a5f;
  color: #fff;
  padding: 0 24px;
  height: 58px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.18);
  z-index: 10;
`;

const OverdueBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${({ $count }) => $count > 0 ? '#ef4444' : 'rgba(255,255,255,0.12)'};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 13px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: ${({ $count }) => $count > 0 ? '#dc2626' : 'rgba(255,255,255,0.2)'}; }
`;

const OverduePanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0,0,0,0.15);
  z-index: 200;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.22s ease;
`;

const OverduePanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #1e3a5f;
  color: #fff;
  flex-shrink: 0;
`;

const OverdueItem = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  &:hover { background: #f8fafc; }
`;

const OverdueOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.25);
  z-index: 199;
`;

const HeaderLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.3px;
  svg { color: #5b9bd5; }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChatDetailBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.12);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.16);
  border-radius: 8px;
  padding: 6px 13px;
  font-size: 13px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
  transition: background 0.2s;
  &:hover {
    background: ${({ disabled }) => (disabled ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.2)')};
  }
`;

const HeaderUserChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: default;
`;

const MainBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

// ─────────────────────────────────────────────
// Left Panel – Teamwork List
// ─────────────────────────────────────────────
const LeftPanel = styled.aside`
  width: 320px;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e3e8ef;
  overflow: hidden;
`;

const PanelHead = styled.div`
  padding: 16px 16px 10px;
  border-bottom: 1px solid #e3e8ef;
`;

const PanelTitle = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: #1e3a5f;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: #f5f7fa;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  padding: 6px 10px;
  gap: 6px;
  svg { color: #9ba6b4; flex-shrink: 0; }
  input {
    border: none;
    background: none;
    outline: none;
    font-size: 13px;
    color: #374151;
    width: 100%;
    &::placeholder { color: #b0b9c4; }
  }
`;

const CreateBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 10px;
  padding: 8px;
  background: #1e3a5f;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s;
  &:hover { background: #14294a; }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px 6px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  flex: 1;
  &::-webkit-scrollbar { display: none; }
`;

const FilterRowWrapper = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e3e8ef;
  padding: 0 4px;
`;

const ScrollArrow = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 4px 3px;
  display: flex;
  align-items: center;
  &:hover { color: #1e3a5f; }
`;

function ScrollableFilterRow({ children }) {
  const rowRef = React.useRef(null);
  const scroll = (dir) => {
    if (rowRef.current) rowRef.current.scrollBy({ left: dir * 80, behavior: 'smooth' });
  };
  return (
    <FilterRowWrapper>
      <ScrollArrow onClick={() => scroll(-1)}><FaChevronLeft size={10} /></ScrollArrow>
      <FilterRow ref={rowRef}>{children}</FilterRow>
      <ScrollArrow onClick={() => scroll(1)}><FaChevronRight size={10} /></ScrollArrow>
    </FilterRowWrapper>
  );
}

const FilterChip = styled.button`
  padding: 4px 10px;
  border-radius: 20px;
  flex-shrink: 0;
  border: 1px solid ${({ $active }) => ($active ? '#1e3a5f' : '#e3e8ef')};
  background: ${({ $active }) => ($active ? '#1e3a5f' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#6b7280')};
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
  &:hover { border-color: #1e3a5f; color: #1e3a5f; background: #f0f4fa; }
  ${({ $active }) => $active && css`&:hover { background: #14294a; color: #fff; }`}
`;

const TeamworkList = styled.ul`
  flex: 1;
  overflow-y: auto;
  list-style: none;
  padding: 6px 0;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
`;

const TeamworkItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  border-left: 3px solid ${({ $active }) => ($active ? '#1e3a5f' : 'transparent')};
  background: ${({ $active }) => ($active ? '#f0f4fa' : 'transparent')};
  transition: all 0.15s;
  animation: ${slideIn} 0.25s ease;
  &:hover { background: #f5f7fb; }
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
`;

const ItemCode = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  color: #5b9bd5;
  text-transform: uppercase;
`;

const PriorityBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  background: ${({ $p }) => $p === 'high' ? '#fee2e2' : $p === 'medium' ? '#fef3c7' : '#d1fae5'};
  color: ${({ $p }) => $p === 'high' ? '#dc2626' : $p === 'medium' ? '#d97706' : '#059669'};
  text-transform: uppercase;
`;

const ItemTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #1f2937;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
`;

const StatusBadge = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  background: ${({ $s }) =>
    $s === 'completed' ? '#dcfce7' :
    $s === 'in-progress' ? '#dbeafe' :
    $s === 'cancelled' ? '#fee2e2' : '#f3f4f6'};
  color: ${({ $s }) =>
    $s === 'completed' ? '#16a34a' :
    $s === 'in-progress' ? '#2563eb' :
    $s === 'cancelled' ? '#dc2626' : '#6b7280'};
  text-transform: capitalize;
`;

const DeadlineText = styled.span`
  font-size: 11px;
  color: ${({ $overdue }) => ($overdue ? '#ef4444' : '#9ca3af')};
  font-weight: ${({ $overdue }) => ($overdue ? 600 : 400)};
`;

const UnreadDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #1e3a5f;
  color: #fff;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  animation: ${pulse} 1.5s ease infinite;
`;

// ─────────────────────────────────────────────
// Center Panel – Chat View
// ─────────────────────────────────────────────
const CenterPanel = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  padding: 12px 20px;
  border-bottom: 1px solid #e3e8ef;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
`;

const ChatHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ChatHeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e3a5f;
`;

const ChatHeaderSub = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ChatHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e3e8ef;
  background: #fff;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f5f7fb; border-color: #c7d0db; color: #1e3a5f; }
`;

const ChatBg = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f0f2f5 url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e2e8f0' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #c7d0db; border-radius: 4px; }
`;

const DateDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #9ca3af;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  &::before, &::after { content: ''; flex: 1; height: 1px; background: #d1d5db; }
`;

const SystemEvent = styled.div`
  display: flex;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;
  span {
    background: rgba(30,58,95,0.07);
    border: 1px solid rgba(30,58,95,0.12);
    border-radius: 20px;
    padding: 4px 14px;
    font-size: 12px;
    color: #4b5563;
    font-weight: 500;
    max-width: 85%;
    text-align: center;
    line-height: 1.5;
  }
`;

const MessageRow = styled.div`
  display: flex;
  flex-direction: ${({ $mine }) => ($mine ? 'row-reverse' : 'row')};
  align-items: flex-end;
  gap: 8px;
  border-radius: 8px;
  padding: 2px 4px;
  animation: ${fadeIn} 0.2s ease;
  ${({ $highlighted }) => $highlighted && css`animation: ${highlightPulse} 1.5s ease forwards;`}
`;

const MsgAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #dbeafe;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Bubble = styled.div`
  max-width: 65%;
  padding: 9px 12px;
  border-radius: ${({ $mine }) => $mine ? '14px 4px 14px 14px' : '4px 14px 14px 14px'};
  background: ${({ $mine }) => $mine ? '#1e3a5f' : '#fff'};
  color: ${({ $mine }) => $mine ? '#fff' : '#1f2937'};
  font-size: 13.5px;
  line-height: 1.55;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  word-break: break-word;
  position: relative;
`;

const BubbleSender = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #5b9bd5;
  margin-bottom: 3px;
`;

const BubbleMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
  font-size: 10.5px;
  color: ${({ $mine }) => $mine ? 'rgba(255,255,255,0.65)' : '#9ca3af'};
`;

const MessageLink = styled.a`
  color: ${({ $mine }) => ($mine ? '#bfdbfe' : '#1d4ed8')};
  text-decoration: underline;
`;

const ReplyBubble = styled.div`
  background: ${({ $mine }) => $mine ? 'rgba(255,255,255,0.15)' : '#f1f5f9'};
  border-left: 3px solid #5b9bd5;
  border-radius: 6px;
  padding: 5px 8px;
  margin-bottom: 6px;
  font-size: 12px;
  color: ${({ $mine }) => $mine ? 'rgba(255,255,255,0.8)' : '#6b7280'};
  cursor: pointer;
  &:hover { opacity: 0.8; }
`;

const AttachmentLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: rgba(255,255,255,0.1);
  border-radius: 8px;
  font-size: 12px;
  color: inherit;
  text-decoration: none;
  border: 1px solid rgba(255,255,255,0.2);
  margin-top: 6px;
  &:hover { background: rgba(255,255,255,0.2); }
`;

const TypingHint = styled.div`
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  padding: 0 8px;
  height: 18px;
`;

const ChatInputArea = styled.form`
  background: #fff;
  border-top: 1px solid #e3e8ef;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ReplyBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background: #f0f4fa;
  border-left: 3px solid #1e3a5f;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  color: #374151;
  gap: 8px;
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const ChatInput = styled.textarea`
  flex: 1;
  background: #f5f7fa;
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 13.5px;
  color: #1f2937;
  resize: none;
  outline: none;
  max-height: 120px;
  min-height: 40px;
  line-height: 1.5;
  font-family: inherit;
  transition: border 0.15s;
  &::placeholder { color: #b0b9c4; }
  &:focus { border-color: #1e3a5f; }
`;

const SendBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #1e3a5f;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.18s;
  flex-shrink: 0;
  &:hover { background: #14294a; }
  &:disabled { background: #c7d0db; cursor: not-allowed; }
`;

const AttachBtn = styled.label`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e3e8ef;
  background: #fff;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
  &:hover { background: #f5f7fb; border-color: #c7d0db; }
`;

const EmptyChat = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: #9ca3af;
  padding: 40px;
  text-align: center;
  svg { font-size: 52px; color: #cbd5e1; }
  h3 { font-size: 17px; color: #374151; font-weight: 600; }
  p { font-size: 13.5px; max-width: 300px; }
`;

// ─────────────────────────────────────────────
// Right Panel – Staff Members
// ─────────────────────────────────────────────
const RightPanel = styled.aside`
  width: 260px;
  min-width: 220px;
  background: #fff;
  border-left: 1px solid #e3e8ef;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StaffListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
`;

const ManageBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 6px;
  border: 1px solid #1e3a5f;
  background: #fff;
  color: #1e3a5f;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #1e3a5f; color: #fff; }
`;

const RemoveBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #fee2e2;
  background: #fff5f5;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid #e3e8ef;
  background: #fafbfc;
  &:hover ${RemoveBtn} { opacity: 1; }
`;

const MemberActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  border: none;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  ${({ $remove }) => $remove ? css`
    background: #fee2e2;
    color: #dc2626;
    &:hover { background: #fecaca; }
  ` : css`
    background: #dcfce7;
    color: #16a34a;
    &:hover { background: #bbf7d0; }
  `}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const StaffRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  border-left: 3px solid ${({ $active }) => ($active ? '#1e3a5f' : 'transparent')};
  background: ${({ $active }) => ($active ? '#f0f4fa' : 'transparent')};
  transition: all 0.15s;
  &:hover { background: #f5f7fb; }
`;

const StaffAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #dbeafe;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const OnlineDot = styled.span`
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #fff;
`;

const StaffInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

const StaffName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StaffRole = styled.div`
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ─────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15,23,42,0.45);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.18s ease;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 28px;
  width: 520px;
  max-width: 95vw;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: #1e3a5f;
  border-bottom: 1px solid #e3e8ef;
  padding-bottom: 12px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-size: 12.5px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  font-size: 13.5px;
  color: #1f2937;
  outline: none;
  transition: border 0.15s;
  font-family: inherit;
  &:focus { border-color: #1e3a5f; }
`;

const TextArea = styled.textarea`
  padding: 9px 12px;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  font-size: 13.5px;
  color: #1f2937;
  outline: none;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: border 0.15s;
  &:focus { border-color: #1e3a5f; }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  font-size: 13.5px;
  color: #1f2937;
  outline: none;
  font-family: inherit;
  transition: border 0.15s;
  background: #fff;
  &:focus { border-color: #1e3a5f; }
`;

const StaffCheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  padding: 8px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
`;

const StaffCheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: #f5f7fb; }
  input { accent-color: #1e3a5f; width: 15px; height: 15px; cursor: pointer; }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 4px;
`;

const Btn = styled.button`
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
  ${({ $variant }) => $variant === 'primary' ? css`
    background: #1e3a5f;
    color: #fff;
    &:hover { background: #14294a; }
    &:disabled { background: #c7d0db; cursor: not-allowed; }
  ` : css`
    background: #f1f5f9;
    color: #374151;
    border: 1px solid #e3e8ef;
    &:hover { background: #e2e8f0; }
  `}
`;

// ─────────────────────────────────────────────
// Detail Drawer (right side overlay)
// ─────────────────────────────────────────────
const DetailDrawer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 340px;
  background: #fff;
  box-shadow: -4px 0 20px rgba(0,0,0,0.1);
  z-index: 50;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} 0.22s ease;
`;

const DrawerHead = styled.div`
  padding: 18px 20px;
  border-bottom: 1px solid #e3e8ef;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const DetailLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const DetailValue = styled.div`
  font-size: 13.5px;
  color: #1f2937;
  font-weight: 500;
`;

const AssigneeChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  background: #f0f4fa;
  border: 1px solid #e3e8ef;
  border-radius: 20px;
  font-size: 12px;
  color: #374151;
  font-weight: 500;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ActivityItem = styled.div`
  display: flex;
  gap: 10px;
  padding-left: 4px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    bottom: -10px;
    width: 2px;
    background: #e3e8ef;
  }
  &:last-child::before { display: none; }
`;

const ActivityDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b9bd5;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #dbeafe;
  margin-top: 4px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const ActivityText = styled.div`
  font-size: 12.5px;
  color: #374151;
  line-height: 1.5;
  span { font-weight: 600; color: #1e3a5f; }
`;

const ActivityTime = styled.div`
  font-size: 11px;
  color: #9ca3af;
`;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const initials = (name) =>
  String(name || '').trim().split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '?';

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date();
  const y = (dt) => dt.toDateString() === today.toDateString();
  const ytd = new Date(today); ytd.setDate(ytd.getDate() - 1);
  if (y(d)) return 'Today';
  if (d.toDateString() === ytd.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtDeadline = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

const isOverdue = (deadline, status) =>
  deadline && status !== 'completed' && status !== 'cancelled' && new Date(deadline) < new Date();

const groupByDate = (messages) => {
  const groups = {};
  messages.forEach((m) => {
    const key = fmtDate(m.created_at);
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  });
  return groups;
};

const messageLinkPattern = /(https?:\/\/[^\s]+)/g;

const renderMessageWithLinks = (text, mine) => {
  const value = String(text || '');
  if (!value) return value;

  return value.split(messageLinkPattern).map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <MessageLink key={`${part}-${index}`} href={part} target="_blank" rel="noreferrer noopener" $mine={mine}>
          {part}
        </MessageLink>
      );
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function Teamwork() {
  const stored = AuthService.getUser();
  const currentUser = stored?.superAdmin || {};
  const currentStaff = currentUser?.staff || {};
  const isSuperAdmin = Boolean(currentUser?.superAdminId);
  const normalizedRole = String(
    currentStaff?.role || currentUser?.role || currentStaff?.staffType || currentStaff?.type || ''
  ).trim().toLowerCase();
  const isAdminUser = isSuperAdmin || normalizedRole === 'admin' || normalizedRole === 'administrator';
  const currentId = isSuperAdmin ? currentUser?.superAdminId : currentStaff?.staffId;
  const currentName = isSuperAdmin
    ? (currentUser?.name || currentStaff?.name || 'Super Admin')
    : (currentStaff?.name || 'Staff');
  const currentType = isSuperAdmin ? 'super_admin' : 'staff';
  const currentImage = currentStaff?.imagePath ? getImageURL(currentStaff.imagePath) : null;

  // State
  const [teamworks, setTeamworks] = useState([]);
  const [selectedTW, setSelectedTW] = useState(null);
  const [messages, setMessages] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [myFilter, setMyFilter] = useState('with_me'); // 'all' | 'with_me' | 'without_me'
  const [search, setSearch] = useState('');
  const [msgText, setMsgText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingTW, setLoadingTW] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [msgError, setMsgError] = useState(null);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [managingMember, setManagingMember] = useState(null); // staffId being added/removed
  const [memberSearch, setMemberSearch] = useState('');
  const [assignedSearch, setAssignedSearch] = useState('');
  const [createSearch, setCreateSearch] = useState('');
  const [deadlineEditOpen, setDeadlineEditOpen] = useState(false);
  const [deadlineEditValue, setDeadlineEditValue] = useState('');
  const [savingDeadline, setSavingDeadline] = useState(false);

  // Create form state
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', deadline: '', assignees: [] });
  const [creating, setCreating] = useState(false);

  const chatEndRef = useRef(null);
  const chatBgRef = useRef(null);
  const socketRef = useRef(null);
  const prevTWIdRef = useRef(null);
  const msgRefsMap = useRef({});
  const isInitialLoadRef = useRef(false);
  const pendingScrollRef = useRef(false);
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);
  const [showOverduePanel, setShowOverduePanel] = useState(false);
  const [copiedTwId, setCopiedTwId] = useState(null);

  // ── Load assignments ──
  const loadAssignments = useCallback(async (twId) => {
    try {
      const res = await axiosInstance.get(`/admin/staff-teamwork/${twId}/assignments`);
      setAssignedStaff(res?.data?.assignments || []);
    } catch {
      setAssignedStaff([]);
    }
  }, []);

  // ── Load teamworks ──
  const loadTeamworks = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/staff-teamwork');
      const list = res?.data?.teamworks || res?.data || [];
      setTeamworks(Array.isArray(list) ? list : []);
      // Build unread map
      const unread = {};
      list.forEach((t) => { if (t.unread_count) unread[t.teamworkId] = t.unread_count; });
      setUnreadCounts(unread);
    } catch {
      setTeamworks([]);
    } finally {
      setLoadingTW(false);
    }
  }, []);

  // ── Load staff list ──
  const loadStaff = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/staff');
      const list = res?.data?.staff || res?.data?.staffMembers || res?.data || [];
      const active = (Array.isArray(list) ? list : []).filter((s) => s.loginStatus !== 'blocked');
      setStaffList(active);
    } catch {
      setStaffList([]);
    }
  }, []);

  // ── Load messages for selected teamwork ──
  const loadMessages = useCallback(async (twId) => {
    if (!twId) return;
    isInitialLoadRef.current = true;
    pendingScrollRef.current = true;
    setLoadingMsg(true);
    setMsgError(null);
    try {
      const res = await axiosInstance.get(`/admin/staff-teamwork/${twId}/messages`);
      console.log('DEBUG teamwork messages response:', res);
      if (res?.data?.error) {
        setMsgError(res.data.error);
        setMessages([]);
      } else {
        const list = res?.data?.messages || res?.data || [];
        setMessages(Array.isArray(list) ? list : []);
        // mark read
        await axiosInstance.post(`/admin/staff-teamwork/${twId}/messages/read`).catch(() => {});
        setUnreadCounts((prev) => ({ ...prev, [twId]: 0 }));
      }
    } catch (err) {
      setMsgError(err?.message || 'Failed to load messages');
      setMessages([]);
    } finally {
      setLoadingMsg(false);
    }
  }, []);

  // ── Load activities ──
  const loadActivities = useCallback(async (twId) => {
    if (!twId) return;
    try {
      const res = await axiosInstance.get(`/admin/staff-teamwork/${twId}/activities`);
      setActivities(res?.data?.activities || res?.data || []);
    } catch {
      setActivities([]);
    }
  }, []);

  useEffect(() => { loadTeamworks(); loadStaff(); }, [loadTeamworks, loadStaff]);

  // ── Socket.IO: connect once on mount ──
  useEffect(() => {
    const socket = socketIO(getNotifURL(), {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    // Join user room for general notifications
    if (currentId) {
      socket.emit('join', { userId: String(currentId) });
    }

    // Listen for live teamwork messages
    socket.on('teamwork_message', ({ teamworkId, message: newMsg }) => {
      setMessages((prev) => {
        // Avoid duplicates (in case we also received via POST response)
        if (prev.some((m) => m.messageId === newMsg.messageId)) return prev;
        return [...prev, newMsg];
      });
      // Mark this teamwork unread if not currently selected
      setUnreadCounts((prev) => {
        const currentId_ = prevTWIdRef.current;
        if (String(currentId_) === String(teamworkId)) return prev;
        return { ...prev, [teamworkId]: (prev[teamworkId] || 0) + 1 };
      });
    });

    // Listen for live status changes from other clients
    socket.on('teamwork_status_changed', ({ teamworkId, status }) => {
      setTeamworks((prev) =>
        prev.map((tw) => (tw.teamworkId === teamworkId ? { ...tw, status } : tw))
      );
      setSelectedTW((prev) => {
        if (!prev || prev.teamworkId !== teamworkId) return prev;
        return { ...prev, status };
      });
    });

    // Listen for live assignment changes from other clients
    socket.on('teamwork_assignments_changed', ({ teamworkId, assignments }) => {
      if (String(prevTWIdRef.current) === String(teamworkId)) {
        setAssignedStaff(assignments);
      }
    });

    return () => {
      socket.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Join/leave teamwork socket room when selection changes ──
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    // Leave previous room
    if (prevTWIdRef.current && prevTWIdRef.current !== selectedTW?.teamworkId) {
      socket.emit('leave_teamwork', { teamworkId: prevTWIdRef.current });
    }

    if (selectedTW) {
      socket.emit('join_teamwork', { teamworkId: selectedTW.teamworkId });
      prevTWIdRef.current = selectedTW.teamworkId;
      loadMessages(selectedTW.teamworkId);
      loadActivities(selectedTW.teamworkId);
      loadAssignments(selectedTW.teamworkId);
    } else {
      prevTWIdRef.current = null;
      setAssignedStaff([]);
    }
  }, [selectedTW, loadMessages, loadActivities, loadAssignments]);

  // Scroll to bottom — fires after loading is done and messages are in DOM
  useLayoutEffect(() => {
    if (!loadingMsg && pendingScrollRef.current) {
      pendingScrollRef.current = false;
      if (chatBgRef.current) {
        chatBgRef.current.scrollTop = chatBgRef.current.scrollHeight;
      }
    }
  }, [loadingMsg, messages]);

  // Smooth scroll for new live messages (not initial load)
  useEffect(() => {
    if (!isInitialLoadRef.current && !loadingMsg) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll teamwork list every 30s (lightweight — just counts/status)
  useEffect(() => {
    const t = setInterval(loadTeamworks, 30000);
    return () => clearInterval(t);
  }, [loadTeamworks]);

  // ── Filtered list ──
  const filteredTeamworks = teamworks.filter((tw) => {
    const matchStatus = filterStatus === 'all' || tw.status === filterStatus;
    const q = search.toLowerCase();
    const matchSearch = !search || tw.title?.toLowerCase().includes(q) || tw.teamwork_code?.toLowerCase().includes(q) || tw.description?.toLowerCase().includes(q);
    const matchMine = !isSuperAdmin || myFilter === 'all'
      || (myFilter === 'with_me' && tw.is_mine == 1)
      || (myFilter === 'without_me' && tw.is_mine != 1);
    return matchStatus && matchSearch && matchMine;
  });

  // ── Send message ──
  const handleSend = async (e) => {
    e.preventDefault();
    if (!msgText.trim() && !e.nativeEvent?.submitter?.dataset?.attach) return;
    if (!selectedTW) return;
    setSendingMsg(true);
    try {
      const payload = {
        message: msgText.trim() || null,
        sender_type: currentType,
        ...(currentType === 'super_admin' ? { sender_super_admin_id: currentId } : { sender_staff_id: currentId }),
        reply_to_message_id: replyTo?.messageId || null,
      };
      const res = await axiosInstance.post(`/admin/staff-teamwork/${selectedTW.teamworkId}/messages`, payload);
      // Socket.IO will broadcast the message to all room members including the sender.
      // However, we also optimistically add the server response for the sender immediately.
      const newMsg = res?.data?.message || null;
      if (newMsg) {
        setMessages((prev) => {
          if (prev.some((m) => m.messageId === newMsg.messageId)) return prev;
          return [...prev, newMsg];
        });
      }
      setMsgText('');
      setReplyTo(null);
    } catch {
      // silent
    } finally {
      setSendingMsg(false);
    }
  };

  // ── Create teamwork ──
  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        deadline: form.deadline || null,
        assignee_staff_ids: form.assignees,
        creator_type: currentType,
        ...(currentType === 'super_admin' ? { created_by_super_admin_id: currentId } : { created_by_staff_id: currentId }),
      };
      await axiosInstance.post('/admin/staff-teamwork', payload);
      setShowCreateModal(false);
      setForm({ title: '', description: '', priority: 'medium', deadline: '', assignees: [] });
      await loadTeamworks();
      signalCountRefresh();
    } catch {
      // silent
    } finally {
      setCreating(false);
    }
  };

  // ── Format date for datetime-local input ──
  const toDatetimeLocal = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // ── Update deadline ──
  const handleDeadlineSave = async () => {
    if (!selectedTW || savingDeadline) return;
    setSavingDeadline(true);
    try {
      const res = await axiosInstance.patch(`/admin/staff-teamwork/${selectedTW.teamworkId}/deadline`, {
        deadline: deadlineEditValue || null,
        actor_name: currentName,
        actor_type: currentType,
        ...(currentType === 'super_admin' ? { actor_super_admin_id: currentId } : { actor_staff_id: currentId }),
      });
      const newDeadline = res?.data?.deadline || null;
      setTeamworks((prev) => prev.map((t) => t.teamworkId === selectedTW.teamworkId ? { ...t, deadline: newDeadline } : t));
      setSelectedTW((t) => ({ ...t, deadline: newDeadline }));
      if (res?.data?.systemMessage) {
        setMessages((prev) => [...prev, res.data.systemMessage]);
      }
      setDeadlineEditOpen(false);
      signalCountRefresh();
    } catch { /* silent */ }
    finally { setSavingDeadline(false); }
  };

  // ── Update status ──
  const handleStatusChange = async (tw, newStatus) => {
    const oldStatus = tw.status;
    try {
      await axiosInstance.patch(`/admin/staff-teamwork/${tw.teamworkId}/status`, {
        status: newStatus,
        old_status: oldStatus,
        actor_name: currentName,
        actor_type: currentType,
        ...(currentType === 'super_admin' ? { actor_super_admin_id: currentId } : { actor_staff_id: currentId }),
      });
      setTeamworks((prev) => prev.map((t) => t.teamworkId === tw.teamworkId ? { ...t, status: newStatus } : t));
      if (selectedTW?.teamworkId === tw.teamworkId) setSelectedTW((t) => ({ ...t, status: newStatus }));
      signalCountRefresh();
    } catch { /* silent */ }
  };

  // ── Can manage members (super admin or creator) ──
  const canManageMembers = selectedTW && (
    isSuperAdmin ||
    (currentType === 'staff' && selectedTW.created_by_staff_id === currentId) ||
    (currentType === 'super_admin' && selectedTW.created_by_super_admin_id === currentId)
  );

  // ── Add / remove assigned member ──
  const handleAddMember = async (staffId) => {
    if (!selectedTW) return;
    setManagingMember(staffId);
    try {
      const res = await axiosInstance.post(`/admin/staff-teamwork/${selectedTW.teamworkId}/assignments`, {
        staff_id: staffId,
        actor_name: currentName,
        actor_type: currentType,
        ...(currentType === 'super_admin' ? { actor_super_admin_id: currentId } : { actor_staff_id: currentId }),
      });
      setAssignedStaff(res?.data?.assignments || []);
      signalCountRefresh();
    } catch (err) {
      console.error('Add member failed:', err?.response?.data || err.message);
    } finally { setManagingMember(null); }
  };

  const handleRemoveMember = async (staffId) => {
    if (!selectedTW) return;
    setManagingMember(staffId);
    try {
      const res = await axiosInstance.delete(`/admin/staff-teamwork/${selectedTW.teamworkId}/assignments/${staffId}`, {
        data: {
          actor_name: currentName,
          actor_type: currentType,
          ...(currentType === 'super_admin' ? { actor_super_admin_id: currentId } : { actor_staff_id: currentId }),
        },
      });
      setAssignedStaff(res?.data?.assignments || []);
      signalCountRefresh();
    } catch (err) {
      console.error('Remove member failed:', err?.response?.data || err.message);
    } finally { setManagingMember(null); }
  };

  // ── Textarea auto-resize ──
  const handleMsgChange = (e) => {
    setMsgText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  // ── Resolve sender name from message ──
  const getSenderName = (msg) => {
    if (msg._sender_name) return msg._sender_name;
    // Fallback: if sender is the currently logged-in super admin
    if (msg.sender_type === 'super_admin' && msg.sender_super_admin_id === currentId) return currentName;
    if (msg.sender_type === 'super_admin') return 'Super Admin';
    const staff = staffList.find((s) => s.staffId === (msg.sender_staff_id || msg.sender_admin_id));
    return staff?.name || 'Staff';
  };

  const getSenderImage = (msg) => {
    if (msg._sender_image) return getImageURL(msg._sender_image);
    // Fallback: current logged-in super admin's own image
    if (msg.sender_type === 'super_admin' && msg.sender_super_admin_id === currentId && currentImage) return currentImage;
    const staff = staffList.find((s) => s.staffId === (msg.sender_staff_id || msg.sender_admin_id));
    return staff?.imagePath ? getImageURL(staff.imagePath) : null;
  };

  const isMyMessage = (msg) => {
    if (currentType === 'super_admin') return msg.sender_super_admin_id === currentId || msg.sender_type === 'super_admin';
    return msg.sender_staff_id === currentId || msg.sender_admin_id === currentId;
  };

  // ── Scroll to replied message (WhatsApp-style) ──
  const scrollToMessage = useCallback((id) => {
    const el = msgRefsMap.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMsgId(id);
      setTimeout(() => setHighlightedMsgId(null), 1500);
    }
  }, []);

  // ── Resolve reply message ──
  const getReplyMsg = (id) => messages.find((m) => m.messageId === id) || null;

  // ── Copy overdue reminder to clipboard ──
  const copyOverdueMessage = (tw, e) => {
    e.stopPropagation();
    const deadlineStr = fmtDeadline(tw.deadline);
    const msg =
`*⚠️ OVERDUE TASK NOTICE — Immediate Response Required*

Dear Team Member,

Please be advised that the following assigned task has *exceeded its deadline* and remains *incomplete*. This requires your immediate attention and a formal explanation.

*📋 Task:* ${tw.title} _(${tw.teamwork_code})_
*🗓 Deadline:* ${deadlineStr}
*🚦 Priority:* ${tw.priority.toUpperCase()}
*🟡 Current Status:* ${tw.status}${tw.description ? `\n*📝 Description:* ${tw.description}` : ''}

You are hereby requested to:
1️⃣ *Provide a written explanation* for the delay in completing this task.
2️⃣ *Submit a revised completion timeline* with clear milestones.
3️⃣ *Ensure immediate action* is taken to resolve any outstanding issues.

Failure to respond within *24 hours* will be escalated to senior management for further action.

*Please treat this as a matter of urgency.*

Regards,
Management`;
    navigator.clipboard.writeText(msg).then(() => {
      setCopiedTwId(tw.teamworkId);
      setTimeout(() => setCopiedTwId(null), 2000);
    });
  };

  // ── Overdue teamworks (deadline passed, not completed/cancelled) ──
  const overdueTeamworks = teamworks.filter((tw) =>
    tw.deadline &&
    new Date(tw.deadline) < new Date() &&
    tw.status !== 'completed' &&
    tw.status !== 'cancelled'
  );

  const openChatDetail = () => {
    if (!selectedTW?.teamworkId) return;
    const url = `/admin/teamwork/chat-detail?teamworkId=${selectedTW.teamworkId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const msgGroups = groupByDate(messages);

  return (
    <>
      <GlobalStyle />
      <PageWrapper>
        {/* ── Top Header ── */}
        <TopHeader>
          <HeaderLogo>
            <FaClipboardList size={22} />
            Staff Teamwork
          </HeaderLogo>
          <HeaderActions>
            {isAdminUser && (
              <OverdueBtn $count={overdueTeamworks.length} onClick={() => setShowOverduePanel(true)} title="Overdue teamworks">
                <FaExclamationTriangle size={13} />
                Overdue
                {overdueTeamworks.length > 0 && <span style={{ background: '#fff', color: '#ef4444', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 700, marginLeft: 2 }}>{overdueTeamworks.length}</span>}
              </OverdueBtn>
            )}
            {isAdminUser && (
              <ChatDetailBtn
                type="button"
                onClick={openChatDetail}
                disabled={!selectedTW?.teamworkId}
                title={selectedTW?.teamworkId ? 'Open chat detail in a new tab' : 'Select a teamwork first'}
              >
                <BiMessageSquareDetail size={15} />
                View Chats Detail
              </ChatDetailBtn>
            )}
          </HeaderActions>
          <HeaderUserChip>
            {currentImage
              ? <img src={currentImage} alt={currentName} style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
              : <BsPersonCircle size={20} />}
            {currentName}
            {isSuperAdmin && <span style={{ background: '#5b9bd5', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>SUPER ADMIN</span>}
          </HeaderUserChip>
        </TopHeader>

        <MainBody>
          {/* ── Overdue Panel ── */}
          {showOverduePanel && (
            <>
              <OverdueOverlay onClick={() => setShowOverduePanel(false)} />
              <OverduePanel>
                <OverduePanelHead>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 15 }}>
                    <FaExclamationTriangle size={15} color="#f87171" />
                    Overdue Teamworks ({overdueTeamworks.length})
                  </div>
                  <button onClick={() => setShowOverduePanel(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><FaTimes size={16} /></button>
                </OverduePanelHead>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {overdueTeamworks.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No overdue teamworks</div>
                  ) : overdueTeamworks.map((tw) => (
                    <OverdueItem key={tw.teamworkId} onClick={() => { setSelectedTW(tw); setShowOverduePanel(false); }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#5b9bd5', fontWeight: 700 }}>{tw.teamwork_code}</span>
                        <PriorityBadge $p={tw.priority}>{tw.priority}</PriorityBadge>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1e3a5f', marginBottom: 3 }}>{tw.title}</div>
                      {tw.description && (
                        <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', marginBottom: 5 }}>
                          {tw.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <StatusBadge $s={tw.status}>{tw.status}</StatusBadge>
                          <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>⚠ Due: {fmtDeadline(tw.deadline)}</span>
                        </div>
                        <button
                          onClick={(e) => copyOverdueMessage(tw, e)}
                          title="Copy reminder message"
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: copiedTwId === tw.teamworkId ? '#dcfce7' : '#f0f4fa', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: copiedTwId === tw.teamworkId ? '#16a34a' : '#1e3a5f', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {copiedTwId === tw.teamworkId ? <><MdCheck size={13} /> Copied!</> : <><MdContentCopy size={13} /> Copy Message</>}
                        </button>
                      </div>
                    </OverdueItem>
                  ))}
                </div>
              </OverduePanel>
            </>
          )}
          {/* ── Left Panel ── */}
          <LeftPanel>
            <PanelHead>
              <PanelTitle><FaClipboardList size={13} /> Teamworks</PanelTitle>
              <SearchBox>
                <FaSearch size={12} />
                <input placeholder="Search teamwork…" value={search} onChange={(e) => setSearch(e.target.value)} />
                {search && <FaTimes size={11} style={{ cursor: 'pointer', color: '#9ca3af' }} onClick={() => setSearch('')} />}
              </SearchBox>
              <CreateBtn onClick={() => setShowCreateModal(true)}>
                <FaPlus size={12} /> New Teamwork
              </CreateBtn>
            </PanelHead>

            <ScrollableFilterRow>
              {['pending', 'in-progress', 'completed', 'cancelled', 'all'].map((s) => (
                <FilterChip key={s} $active={filterStatus === s} onClick={() => setFilterStatus(s)}>
                  {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </FilterChip>
              ))}
            </ScrollableFilterRow>

            {isSuperAdmin && (
              <ScrollableFilterRow>
                {[['with_me', 'With Me'], ['without_me', 'Without Me'], ['all', 'Everyone']].map(([val, label]) => (
                  <FilterChip key={val} $active={myFilter === val} onClick={() => setMyFilter(val)}>
                    {label}
                  </FilterChip>
                ))}
              </ScrollableFilterRow>
            )}

            <TeamworkList>
              {loadingTW ? (
                <li style={{ listStyle: 'none' }}>
                  <LoaderWrap>
                    <SpinRing />
                    <LoaderLabel>Loading teamworks…</LoaderLabel>
                  </LoaderWrap>
                </li>
              ) : filteredTeamworks.length === 0 ? (
                <li style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No teamworks found</li>
              ) : filteredTeamworks.map((tw) => (
                <TeamworkItem key={tw.teamworkId} $active={selectedTW?.teamworkId === tw.teamworkId} onClick={() => setSelectedTW(tw)}>
                  <ItemHeader>
                    <ItemCode>{tw.teamwork_code}</ItemCode>
                    <PriorityBadge $p={tw.priority}>{tw.priority}</PriorityBadge>
                  </ItemHeader>
                  <ItemTitle>{tw.title}</ItemTitle>
                  {tw.description && (
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {tw.description}
                    </div>
                  )}
                  <ItemMeta>
                    <StatusBadge $s={tw.status}>{tw.status}</StatusBadge>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {tw.deadline && (
                        <DeadlineText $overdue={isOverdue(tw.deadline, tw.status)}>
                          {isOverdue(tw.deadline, tw.status) ? '⚠ ' : ''}{fmtDeadline(tw.deadline)}
                        </DeadlineText>
                      )}
                      {unreadCounts[tw.teamworkId] > 0 && <UnreadDot>{unreadCounts[tw.teamworkId]}</UnreadDot>}
                    </div>
                  </ItemMeta>
                </TeamworkItem>
              ))}
            </TeamworkList>
          </LeftPanel>

          {/* ── Center Panel ── */}
          <CenterPanel style={{ position: 'relative' }}>
            {!selectedTW ? (
              <EmptyChat>
                <BiMessageSquareDetail />
                <h3>Select a Teamwork</h3>
                <p>Choose a teamwork from the list to view the chat and collaboration thread.</p>
              </EmptyChat>
            ) : (
              <>
                {/* Chat Header */}
                <ChatHeader>
                  <ChatHeaderInfo>
                    <ChatHeaderTitle>
                      <span style={{ color: '#5b9bd5', fontSize: 13, marginRight: 8 }}>{selectedTW.teamwork_code}</span>
                      {selectedTW.title}
                    </ChatHeaderTitle>
                    {selectedTW.description && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', maxWidth: '100%' }}>
                        {selectedTW.description}
                      </div>
                    )}
                    <ChatHeaderSub>
                      <StatusBadge $s={selectedTW.status}>{selectedTW.status}</StatusBadge>
                      <PriorityBadge $p={selectedTW.priority}>{selectedTW.priority}</PriorityBadge>
                      {(selectedTW.deadline || isSuperAdmin) && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {selectedTW.deadline && (
                            <DeadlineText $overdue={isOverdue(selectedTW.deadline, selectedTW.status)}>
                              Due: {fmtDeadline(selectedTW.deadline)}
                            </DeadlineText>
                          )}
                          {isSuperAdmin && !deadlineEditOpen && (
                            <button
                              onClick={() => { setDeadlineEditValue(toDatetimeLocal(selectedTW.deadline)); setDeadlineEditOpen(true); }}
                              title="Edit deadline"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px 4px', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}
                            >
                              <FaPencilAlt size={11} />
                            </button>
                          )}
                          {isSuperAdmin && deadlineEditOpen && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="datetime-local"
                                value={deadlineEditValue}
                                onChange={(e) => setDeadlineEditValue(e.target.value)}
                                style={{ fontSize: 12, border: '1px solid #d1d5db', borderRadius: 6, padding: '2px 6px', outline: 'none', color: '#374151' }}
                                autoFocus
                              />
                              <button
                                onClick={handleDeadlineSave}
                                disabled={savingDeadline}
                                style={{ background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                              >{savingDeadline ? '…' : 'Save'}</button>
                              <button
                                onClick={() => setDeadlineEditOpen(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px 4px', fontSize: 12 }}
                              >Cancel</button>
                            </span>
                          )}
                        </span>
                      )}
                    </ChatHeaderSub>
                  </ChatHeaderInfo>
                  <ChatHeaderActions>
                    {/* Non-admin: hide dropdown once completed/cancelled */}
                    {/* Admin/Super admin: always show so they can reopen */}
                    {(selectedTW.status !== 'completed' && selectedTW.status !== 'cancelled') || isSuperAdmin ? (
                      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <select
                          style={{ padding: '5px 10px', border: `1px solid ${selectedTW.status === 'completed' ? '#bbf7d0' : selectedTW.status === 'cancelled' ? '#fecaca' : '#e3e8ef'}`, borderRadius: 8, fontSize: 12, color: '#374151', cursor: 'pointer', outline: 'none', background: selectedTW.status === 'completed' ? '#f0fdf4' : selectedTW.status === 'cancelled' ? '#fff1f2' : '#fff' }}
                          value={selectedTW.status}
                          onChange={(e) => handleStatusChange(selectedTW, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        {(selectedTW.status === 'completed' || selectedTW.status === 'cancelled') && isSuperAdmin && (
                          <span style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic', whiteSpace: 'nowrap' }}>Admin override</span>
                        )}
                      </div>
                    ) : (
                      <span style={{ padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: selectedTW.status === 'completed' ? '#dcfce7' : '#fee2e2', color: selectedTW.status === 'completed' ? '#16a34a' : '#dc2626' }}>
                        {selectedTW.status.charAt(0).toUpperCase() + selectedTW.status.slice(1)}
                      </span>
                    )}
                    <IconBtn onClick={() => { setShowDetail(!showDetail); if (!showDetail) loadActivities(selectedTW.teamworkId); }} title="Details & Activity">
                      <FaEllipsisV size={14} />
                    </IconBtn>
                  </ChatHeaderActions>
                </ChatHeader>

                {/* Chat Messages */}
                <ChatBg ref={chatBgRef}>
                  {loadingMsg ? (
                    <LoaderWrap style={{ flex: 1, height: '100%' }}>
                      <SpinRing $size={36} />
                      <LoaderLabel>Loading messages…</LoaderLabel>
                    </LoaderWrap>
                  ) : msgError ? (
                    <div style={{ textAlign: 'center', color: '#ef4444', padding: 24, fontSize: 13 }}>Error: {msgError}</div>
                  ) : messages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 13 }}>No messages yet. Start the conversation!</div>
                  ) : (
                    Object.entries(msgGroups).map(([date, msgs]) => (
                      <React.Fragment key={date}>
                        <DateDivider>{date}</DateDivider>
                        {msgs.map((msg) => {
                          // System event message (status change, etc.)
                          if (msg.message?.startsWith('__SYSTEM__:')) {
                            const text = msg.message.replace('__SYSTEM__:', '');
                            return (
                              <SystemEvent key={msg.messageId}>
                                <span>{text} · {fmtTime(msg.created_at)}</span>
                              </SystemEvent>
                            );
                          }
                          const mine = isMyMessage(msg);
                          const senderName = getSenderName(msg);
                          const senderImg = getSenderImage(msg);
                          const replyMsg = msg.reply_to_message_id ? getReplyMsg(msg.reply_to_message_id) : null;
                          return (
                            <MessageRow
                              key={msg.messageId}
                              $mine={mine}
                              $highlighted={highlightedMsgId === msg.messageId}
                              ref={(el) => { if (el) msgRefsMap.current[msg.messageId] = el; else delete msgRefsMap.current[msg.messageId]; }}
                            >
                              <MsgAvatar>
                                {senderImg ? <img src={senderImg} alt={senderName} /> : initials(senderName)}
                              </MsgAvatar>
                              <Bubble $mine={mine}>
                                {!mine && <BubbleSender>{senderName}</BubbleSender>}
                                {replyMsg && (
                                  <ReplyBubble $mine={mine} onClick={() => scrollToMessage(replyMsg.messageId)}>
                                    ↩ {getSenderName(replyMsg)}: {String(replyMsg.message || '').slice(0, 80)}{replyMsg.message?.length > 80 ? '…' : ''}
                                  </ReplyBubble>
                                )}
                                {msg.message && (
                                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {renderMessageWithLinks(msg.message, mine)}
                                  </div>
                                )}
                                {msg.attachment_link && (
                                  <AttachmentLink href={msg.attachment_link} target="_blank" rel="noopener noreferrer">
                                    <MdOutlineAttachFile size={14} /> Attachment
                                  </AttachmentLink>
                                )}
                                <BubbleMeta $mine={mine}>
                                  {fmtTime(msg.created_at)}
                                  {mine && (
                                    msg.is_all_read
                                      ? <MdDoneAll size={13} style={{ color: '#60a5fa' }} title="All read" />
                                      : <MdCheck size={13} title="Sent" />
                                  )}
                                </BubbleMeta>
                              </Bubble>
                              {!mine && (
                                <button
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 11, padding: 2 }}
                                  onClick={() => setReplyTo(msg)}
                                  title="Reply"
                                >↩</button>
                              )}
                            </MessageRow>
                          );
                        })}
                      </React.Fragment>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </ChatBg>

                <TypingHint />

                {/* Input Area */}
                {['cancelled', 'completed'].includes(selectedTW?.status) ? (
                  <div style={{ padding: '12px 16px', background: '#f1f3f5', borderTop: '1px solid #dde3ea', textAlign: 'center', fontSize: 13, color: '#8a97a8' }}>
                    Chat is closed — this task is <strong>{selectedTW.status}</strong>.
                  </div>
                ) : (
                <ChatInputArea onSubmit={handleSend}>
                  {replyTo && (
                    <ReplyBar>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 11, color: '#1e3a5f', marginBottom: 2 }}>↩ Replying to {getSenderName(replyTo)}</div>
                        <div>{String(replyTo.message || '').slice(0, 100)}{(replyTo.message?.length || 0) > 100 ? '…' : ''}</div>
                      </div>
                      <FaTimes size={12} style={{ cursor: 'pointer', flexShrink: 0, marginTop: 2 }} onClick={() => setReplyTo(null)} />
                    </ReplyBar>
                  )}
                  <InputRow>
                    <AttachBtn title="Attach file (URL)">
                      <MdOutlineAttachFile size={18} />
                    </AttachBtn>
                    <ChatInput
                      placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                      value={msgText}
                      onChange={handleMsgChange}
                      onKeyDown={handleKeyDown}
                      rows={1}
                    />
                    <SendBtn type="submit" disabled={!msgText.trim() || sendingMsg} title="Send">
                      <FaPaperPlane size={15} />
                    </SendBtn>
                  </InputRow>
                </ChatInputArea>
                )}

                {/* Detail Drawer */}
                {showDetail && (
                  <DetailDrawer>
                    <DrawerHead>
                      <span style={{ fontWeight: 700, color: '#1e3a5f', fontSize: 15 }}>Details & Activity</span>
                      <IconBtn onClick={() => setShowDetail(false)}><FaTimes size={13} /></IconBtn>
                    </DrawerHead>
                    <DrawerContent>
                      <DetailRow>
                        <DetailLabel>Code</DetailLabel>
                        <DetailValue>{selectedTW.teamwork_code}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Title</DetailLabel>
                        <DetailValue>{selectedTW.title}</DetailValue>
                      </DetailRow>
                      {selectedTW.description && (
                        <DetailRow>
                          <DetailLabel>Description</DetailLabel>
                          <DetailValue style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{selectedTW.description}</DetailValue>
                        </DetailRow>
                      )}
                      <DetailRow>
                        <DetailLabel>Status</DetailLabel>
                        <StatusBadge $s={selectedTW.status}>{selectedTW.status}</StatusBadge>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Priority</DetailLabel>
                        <PriorityBadge $p={selectedTW.priority}>{selectedTW.priority}</PriorityBadge>
                      </DetailRow>
                      {selectedTW.deadline && (
                        <DetailRow>
                          <DetailLabel>Deadline</DetailLabel>
                          <DeadlineText $overdue={isOverdue(selectedTW.deadline, selectedTW.status)}>
                            {fmtDeadline(selectedTW.deadline)} {isOverdue(selectedTW.deadline, selectedTW.status) ? '(Overdue)' : ''}
                          </DeadlineText>
                        </DetailRow>
                      )}
                      {selectedTW.assignees?.length > 0 && (
                        <DetailRow>
                          <DetailLabel>Assignees</DetailLabel>
                          <AssigneeChips>
                            {selectedTW.assignees.map((a) => (
                              <Chip key={a.staffId || a.id}>
                                <FaCircle size={7} style={{ color: '#22c55e' }} /> {a.name || a.staff_name || `Staff #${a.staffId}`}
                              </Chip>
                            ))}
                          </AssigneeChips>
                        </DetailRow>
                      )}
                      <DetailRow>
                        <DetailLabel>Activity Log</DetailLabel>
                        <ActivityList>
                          {activities.length === 0
                            ? <div style={{ fontSize: 12.5, color: '#9ca3af' }}>No activity recorded yet.</div>
                            : activities.map((act) => (
                              <ActivityItem key={act.id}>
                                <ActivityDot />
                                <div>
                                  <ActivityText>
                                    <span>{act.description || act.activity_type}</span>
                                    {act.old_value && act.new_value && (
                                      <span style={{ fontWeight: 400, color: '#6b7280' }}> · {act.old_value} → {act.new_value}</span>
                                    )}
                                  </ActivityText>
                                  <ActivityTime>{fmtDate(act.created_at)} {fmtTime(act.created_at)}</ActivityTime>
                                </div>
                              </ActivityItem>
                            ))
                          }
                        </ActivityList>
                      </DetailRow>
                    </DrawerContent>
                  </DetailDrawer>
                )}
              </>
            )}
          </CenterPanel>

          {/* ── Right Panel – Assigned Members ── */}
          <RightPanel>
            <PanelHead>
              <PanelTitle><FaUsers size={13} /> {selectedTW ? 'Assigned Members' : 'Staff Members'}</PanelTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  {selectedTW ? assignedStaff.length : staffList.length}
                </span>
                {canManageMembers && (
                  <ManageBtn title="Manage Members" onClick={() => { setMemberSearch(''); setAssignedSearch(''); setShowMembersModal(true); }}>
                    <FaUserPlus size={12} /> Manage
                  </ManageBtn>
                )}
              </div>
            </PanelHead>
            <StaffListScroll>
              {!selectedTW ? (
                staffList.length === 0
                  ? <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12.5 }}>No staff found</div>
                  : staffList.map((s) => (
                    <StaffRow key={s.staffId}>
                      <StaffAvatar>
                        {s.imagePath ? <img src={getImageURL(s.imagePath)} alt={s.name} /> : initials(s.name)}
                      </StaffAvatar>
                      <StaffInfo>
                        <StaffName>{s.name}</StaffName>
                        <StaffRole>{s.type || '—'}</StaffRole>
                      </StaffInfo>
                    </StaffRow>
                  ))
              ) : assignedStaff.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontSize: 12.5 }}>No members assigned</div>
              ) : (
                assignedStaff.map((s) => (
                  <StaffRow key={s.staff_id}>
                    <StaffAvatar>
                      {s.imagePath ? <img src={getImageURL(s.imagePath)} alt={s.name} /> : initials(s.name)}
                      <OnlineDot />
                    </StaffAvatar>
                    <StaffInfo>
                      <StaffName>{s.name}</StaffName>
                      <StaffRole>{s.type || '—'}</StaffRole>
                    </StaffInfo>
                    {canManageMembers && (
                      <RemoveBtn
                        title="Remove member"
                        disabled={managingMember === s.staff_id}
                        onClick={() => handleRemoveMember(s.staff_id)}
                      >
                        <FaUserMinus size={11} />
                      </RemoveBtn>
                    )}
                  </StaffRow>
                ))
              )}
            </StaffListScroll>
          </RightPanel>
        </MainBody>
      </PageWrapper>

      {/* ── Create Teamwork Modal ── */}
      {showCreateModal && (
        <Overlay onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
          <Modal>
            <ModalTitle>Create New Teamwork</ModalTitle>
            <FieldGroup>
              <Label>Title *</Label>
              <Input
                placeholder="Enter teamwork title…"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </FieldGroup>
            <FieldGroup>
              <Label>Description</Label>
              <TextArea
                placeholder="Describe the teamwork objective…"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </FieldGroup>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FieldGroup>
                <Label>Priority</Label>
                <Select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </Select>
              </FieldGroup>
              <FieldGroup>
                <Label>Deadline</Label>
                <Input type="datetime-local" value={form.deadline} onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))} />
              </FieldGroup>
            </div>
            <FieldGroup>
              <Label>Assign Staff Members</Label>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <FaSearch size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                <input
                  placeholder="Search staff…"
                  value={createSearch}
                  onChange={(e) => setCreateSearch(e.target.value)}
                  style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, border: '1px solid #e3e8ef', fontSize: 13, outline: 'none', color: '#1f2937' }}
                />
              </div>
              <StaffCheckList>
                {staffList.length === 0
                  ? <div style={{ fontSize: 12.5, color: '#9ca3af', padding: '6px 4px' }}>No staff available</div>
                  : staffList
                    .filter((s) => !createSearch.trim() || s.name.toLowerCase().includes(createSearch.toLowerCase()) || (s.type || '').toLowerCase().includes(createSearch.toLowerCase()))
                    .map((s) => (
                    <StaffCheckRow key={s.staffId}>
                      <input
                        type="checkbox"
                        checked={form.assignees.includes(s.staffId)}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          assignees: e.target.checked
                            ? [...f.assignees, s.staffId]
                            : f.assignees.filter((id) => id !== s.staffId),
                        }))}
                      />
                      <StaffAvatar style={{ width: 26, height: 26, fontSize: 10 }}>
                        {s.imagePath ? <img src={getImageURL(s.imagePath)} alt={s.name} /> : initials(s.name)}
                      </StaffAvatar>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{s.type || s.staffType || '—'}</div>
                      </div>
                    </StaffCheckRow>
                  ))
                }
              </StaffCheckList>
            </FieldGroup>
            <ModalActions>
              <Btn onClick={() => { setShowCreateModal(false); setCreateSearch(''); }}>Cancel</Btn>
              <Btn $variant="primary" onClick={handleCreate} disabled={creating || !form.title.trim()}>
                {creating ? 'Creating…' : 'Create Teamwork'}
              </Btn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}

      {/* ── Manage Members Modal ── */}
      {showMembersModal && selectedTW && (
        <Overlay onClick={(e) => { if (e.target === e.currentTarget) { setShowMembersModal(false); setMemberSearch(''); setAssignedSearch(''); } }}>
          <Modal style={{ maxWidth: 440 }}>
            <ModalTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Manage Members</span>
              <FaTimes size={14} style={{ cursor: 'pointer', color: '#6b7280' }} onClick={() => setShowMembersModal(false)} />
            </ModalTitle>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              {selectedTW.teamwork_code} · {selectedTW.title}
            </div>

            {/* Currently assigned */}
            <FieldGroup>
              <Label>Currently Assigned ({assignedStaff.length})</Label>
              {assignedStaff.length === 0 ? (
                <div style={{ fontSize: 12.5, color: '#9ca3af', padding: '8px 4px' }}>No members assigned yet.</div>
              ) : (
                <>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <FaSearch size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                    <input
                      placeholder="Search assigned…"
                      value={assignedSearch}
                      onChange={(e) => setAssignedSearch(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, border: '1px solid #e3e8ef', fontSize: 13, outline: 'none', color: '#1f2937' }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 4, maxHeight: 200, overflowY: 'auto' }}>
                    {assignedStaff
                      .filter((s) => !assignedSearch.trim() || s.name.toLowerCase().includes(assignedSearch.toLowerCase()) || (s.type || '').toLowerCase().includes(assignedSearch.toLowerCase()))
                      .map((s) => (
                    <MemberRow key={s.staff_id}>
                      <StaffAvatar style={{ width: 30, height: 30, fontSize: 11 }}>
                        {s.imagePath ? <img src={getImageURL(s.imagePath)} alt={s.name} /> : initials(s.name)}
                      </StaffAvatar>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{s.type || '—'}</div>
                      </div>
                      <MemberActionBtn
                        $remove
                        disabled={managingMember === s.staff_id}
                        onClick={() => handleRemoveMember(s.staff_id)}
                        title="Remove"
                      >
                        {managingMember === s.staff_id ? '…' : <><FaUserMinus size={11} /> Remove</>}
                      </MemberActionBtn>
                    </MemberRow>
                    ))}
                  </div>
                </>
              )}
            </FieldGroup>

            {/* Add from remaining staff */}
            {staffList.filter((s) => !assignedStaff.some((a) => a.staff_id === s.staffId)).length > 0 && (
              <FieldGroup>
                <Label>Add Staff</Label>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <FaSearch size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                  <input
                    placeholder="Search staff…"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, border: '1px solid #e3e8ef', fontSize: 13, outline: 'none', color: '#1f2937' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
                  {staffList
                    .filter((s) => !assignedStaff.some((a) => a.staff_id === s.staffId))
                    .filter((s) => !memberSearch.trim() || s.name.toLowerCase().includes(memberSearch.toLowerCase()) || (s.type || '').toLowerCase().includes(memberSearch.toLowerCase()))
                    .map((s) => (
                      <MemberRow key={s.staffId}>
                        <StaffAvatar style={{ width: 30, height: 30, fontSize: 11 }}>
                          {s.imagePath ? <img src={getImageURL(s.imagePath)} alt={s.name} /> : initials(s.name)}
                        </StaffAvatar>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{s.name}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{s.type || '—'}</div>
                        </div>
                        <MemberActionBtn
                          disabled={managingMember === s.staffId}
                          onClick={() => handleAddMember(s.staffId)}
                          title="Add"
                        >
                          {managingMember === s.staffId ? '…' : <><FaUserPlus size={11} /> Add</>}
                        </MemberActionBtn>
                      </MemberRow>
                    ))
                  }
                </div>
              </FieldGroup>
            )}

            <ModalActions>
              <Btn $variant="primary" onClick={() => { setShowMembersModal(false); setMemberSearch(''); setAssignedSearch(''); }}>Done</Btn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </>
  );
}
